/**
 * Diagnosis kecepatan presensi pegawai.
 *
 * Mencatat lama setiap langkah jaringan saat presensi (token, cek izin,
 * simpan, dst.) supaya kita tahu PERSIS langkah mana yang lambat di HP guru
 * tertentu (mis. hanya lambat di data seluler).
 *
 * - Langkah yang sedang berjalan dipancarkan lewat event `presensi_diag`
 *   (ditampilkan live di modal "Menyimpan presensi…").
 * - Ringkasan proses terakhir disimpan di localStorage (`presensi_diag_last`)
 *   dan ditampilkan kecil di modal hasil, sehingga guru cukup mengirim
 *   tangkapan layar.
 */

export interface DiagStep {
    label: string;
    ms: number;
    ok: boolean;
}

export interface DiagTrace {
    startedAt: string;          // ISO
    totalMs: number;            // jumlah lama semua langkah jaringan (tanpa waktu mengetik alasan)
    steps: DiagStep[];
    network: string;            // mis. "4g · rtt 350ms · 1.2Mbps" (jika browser mendukung)
    tokenSource?: string;       // 'memori' | 'tersimpan' | 'cloud-function'
}

const STORAGE_KEY = 'presensi_diag_last';
export const DIAG_EVENT = 'presensi_diag';

let current: { start: number; startedAt: string; steps: DiagStep[]; tokenSource?: string } | null = null;

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Info koneksi dari Network Information API (Chrome/Android). */
export const describeNetwork = (): string => {
    const c: any = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    if (!navigator.onLine) return 'offline';
    if (!c) return 'tidak diketahui';
    const parts: string[] = [];
    if (c.type && c.type !== 'unknown') parts.push(c.type === 'cellular' ? 'data seluler' : c.type);
    if (c.effectiveType) parts.push(c.effectiveType);
    if (typeof c.rtt === 'number') parts.push(`rtt ${c.rtt}ms`);
    if (typeof c.downlink === 'number') parts.push(`${c.downlink}Mbps`);
    if (c.saveData) parts.push('hemat-data');
    return parts.join(' · ') || 'tidak diketahui';
};

const emit = (label: string | null) => {
    try {
        window.dispatchEvent(new CustomEvent(DIAG_EVENT, { detail: { label, startedAt: current?.start ?? null } }));
    } catch { /* noop */ }
};

export const isDiagActive = (): boolean => current !== null;

/** Mulai mencatat satu proses presensi (menimpa catatan sebelumnya). */
export const startDiag = (): void => {
    current = { start: now(), startedAt: new Date().toISOString(), steps: [] };
    emit('Memulai');
};

/** Catat sumber token Supabase yang dipakai (untuk diagnosis). */
export const setDiagTokenSource = (source: string, overwrite = false): void => {
    if (current && (overwrite || !current.tokenSource)) current.tokenSource = source;
};

/**
 * Bungkus satu langkah jaringan. Lama & hasil (berhasil/gagal) dicatat,
 * error tetap diteruskan ke pemanggil.
 */
export const diagStep = async <T,>(label: string, fn: () => Promise<T>): Promise<T> => {
    if (!current) return fn();
    const t0 = now();
    emit(label);
    try {
        const result = await fn();
        // Supabase TIDAK melempar error (termasuk timeout) — error dikembalikan
        // sebagai `{ error }`. Tandai langkah itu gagal (✗) juga.
        const returnedError = !!(result && typeof result === 'object' && (result as any).error);
        current?.steps.push({ label, ms: Math.round(now() - t0), ok: !returnedError });
        return result;
    } catch (err) {
        current?.steps.push({ label, ms: Math.round(now() - t0), ok: false });
        throw err;
    }
};

/** Selesaikan pencatatan & simpan ke localStorage. */
export const finishDiag = (): DiagTrace | null => {
    if (!current) return null;
    const trace: DiagTrace = {
        startedAt: current.startedAt,
        // Jumlah lama langkah jaringan saja — waktu pegawai mengetik alasan
        // terlambat/pulang cepat TIDAK ikut dihitung.
        totalMs: current.steps.reduce((sum, st) => sum + st.ms, 0),
        steps: current.steps,
        network: describeNetwork(),
        tokenSource: current.tokenSource,
    };
    current = null;
    emit(null);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(trace)); } catch { /* noop */ }
    return trace;
};

export const getLastDiag = (): DiagTrace | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as DiagTrace) : null;
    } catch {
        return null;
    }
};

const sec = (ms: number) => `${(ms / 1000).toFixed(1)}dtk`;

/** Ringkasan satu baris, mis. "Total 2.3dtk · Token 0.0dtk · Simpan 2.1dtk ✗ · 4g · rtt 300ms". */
export const formatDiag = (trace: DiagTrace | null): string => {
    if (!trace) return '';
    const steps = trace.steps.map(s => `${s.label} ${sec(s.ms)}${s.ok ? '' : ' ✗'}`).join(' · ');
    const token = trace.tokenSource ? ` · token: ${trace.tokenSource}` : '';
    return `Total ${sec(trace.totalMs)}${steps ? ' · ' + steps : ''}${token} · ${trace.network}`;
};

/**
 * Sinyal batas waktu (AbortSignal) yang juga jalan di browser lama
 * (AbortSignal.timeout baru ada di Chrome 103 / Safari 16).
 */
export const timeoutSignal = (ms: number): AbortSignal => {
    const anyAbort = AbortSignal as any;
    if (typeof anyAbort.timeout === 'function') return anyAbort.timeout(ms);
    const controller = new AbortController();
    setTimeout(() => controller.abort(), ms);
    return controller.signal;
};
